import { useMutation, useQueryClient } from 'react-query'
import { UseMutationOptions } from 'react-query/types/react/types'
import { ReferenceImage } from 'types'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'

export interface Variables {
  scheduleId: number
  contentId: number
}

export interface Data {
  reference_image: ReferenceImage
}

export interface Options extends Omit<UseMutationOptions<ReferenceImage, Error, Variables>, 'mutationFn'> {}

export const createReferenceImage = async (scheduleId: number, contentId: number) => {
  const response = await sendToApi<Data>(`/schedules/${scheduleId}/contents/${contentId}/reference_image`, {
    method: 'POST',
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(buildErrorMessage(response.data.errors) || 'Failed to create reference image')
  }

  return response.data.reference_image
}

export const useCreateReferenceImage = (options?: Options) => {
  const queryClient = useQueryClient()
  return useMutation<ReferenceImage, Error, Variables>(
    async ({ scheduleId, contentId }) => createReferenceImage(scheduleId, contentId),
    {
      ...options,
      onSuccess: async (data, variables, context) => {
        await queryClient.invalidateQueries(scheduleKeys.referenceImage(variables.scheduleId, variables.contentId))
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
